import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';

/**
 * Cabecalho fixo com o host atual, mais atalhos para pular entre eles.
 *
 * Resolve a perda de contexto ao rolar: numa coleta de 15 equipamentos o nome do
 * host aparece uma vez, la em cima, e depois some — o operador desce cem linhas e
 * nao sabe mais de quem esta lendo a saida. Aqui o nome acompanha a rolagem, e os
 * atalhos levam direto a secao de cada um.
 *
 * `secoes` vem do servidor com o indice da linha em que cada host comeca; o dono
 * de cada linha nao e adivinhado pelo separador de texto.
 */
export default function NavegadorDeHosts({ secoes, containerRef, alturaLinha = 19, children }) {
  const [atual, setAtual] = useState(0);
  const refsLinha = useRef({});

  // Qual secao esta no topo da area visivel.
  const aoRolar = useCallback(() => {
    const box = containerRef.current;
    if (!box || !secoes?.length) return;

    const topo = box.scrollTop;
    let achado = 0;
    for (let i = 0; i < secoes.length; i += 1) {
      const el = refsLinha.current[secoes[i].inicio];
      const offset = el ? el.offsetTop - box.offsetTop : secoes[i].inicio * alturaLinha;
      if (offset <= topo + 4) achado = i;
      else break;
    }
    setAtual(achado);
  }, [secoes, containerRef, alturaLinha]);

  useEffect(() => {
    const box = containerRef.current;
    if (!box) return undefined;
    box.addEventListener('scroll', aoRolar, { passive: true });
    return () => box.removeEventListener('scroll', aoRolar);
  }, [containerRef, aoRolar]);

  const irPara = (indice) => {
    const box = containerRef.current;
    const el = refsLinha.current[secoes[indice].inicio];
    if (!box) return;
    if (el) box.scrollTop = el.offsetTop - box.offsetTop;
    else box.scrollTop = secoes[indice].inicio * alturaLinha;
    setAtual(indice);
  };

  const registrar = (inicio) => (el) => {
    if (el) refsLinha.current[inicio] = el;
  };

  const sec = secoes?.[atual];

  /**
   * O cabecalho e devolvido para o filho renderizar DENTRO da caixa de rolagem.
   *
   * Fora dela, `position: sticky` gruda na pagina e nao na caixa: o cabecalho
   * fica parado enquanto o conteudo rola por baixo, e o texto de um atravessa o
   * do outro. Sticky so funciona em relacao ao ancestral que rola.
   */
  const cabecalho = sec ? (
    <Box
      sx={{
        position: 'sticky', top: 0, zIndex: 4,
        bgcolor: '#e2e8f0', color: '#1e293b',
        borderBottom: '1px solid #cbd5e1',
        px: 1.25, py: 0.4,
        display: 'flex', alignItems: 'center', gap: 1,
      }}
    >
      <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{sec.nome}</Typography>
      <Typography variant="caption" sx={{ color: '#64748b' }}>
        {sec.diferentes ? `${sec.diferentes} diferença(s)` : 'sem diferenças'}
      </Typography>
      <Box sx={{ flex: 1 }} />
      <Typography variant="caption" sx={{ color: '#64748b' }}>
        {atual + 1} de {secoes.length}
      </Typography>
    </Box>
  ) : null;

  return (
    <>
      <Stack direction="row" spacing={0.75} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        {(secoes || []).map((s, i) => (
          <Tooltip
            key={s.inicio}
            title={s.diferentes ? `${s.diferentes} linha(s) diferentes` : 'sem diferenças'}
          >
            <Chip
              size="small"
              label={
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <span>{s.nome}</span>
                  {!!s.diferentes && (
                    <Box component="span" sx={{ fontWeight: 700 }}>{s.diferentes}</Box>
                  )}
                </Stack>
              }
              onClick={() => irPara(i)}
              sx={{
                height: 22, cursor: 'pointer',
                bgcolor: i === atual ? '#475569' : '#fff',
                color: i === atual ? '#fff' : s.diferentes ? '#92400e' : '#64748b',
                border: `1px solid ${i === atual ? '#475569' : s.diferentes ? '#fcd34d' : '#e2e8f0'}`,
              }}
            />
          </Tooltip>
        ))}
      </Stack>

      {children(registrar, cabecalho)}
    </>
  );
}
